import { useRef, useState } from "react";
import { Upload, X, Library } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import MediaLibraryModal from "./admin/MediaLibraryModal";
import FocalPointPicker from "./admin/FocalPointPicker";
import { useUploadStatus } from "../context/UploadStatusContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Image upload + URL input combo for admin forms.
 * - Allows pasting a URL OR uploading a file (image is uploaded to object storage)
 * - Resolved value is always a fully-qualified URL stored on the parent form
 */
export default function ImageUpload({ value, onChange, label = "Image", testIdPrefix = "image" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [libraryOpen, setLibraryOpen] = useState(false);
    const { begin: beginUpload, end: endUpload } = useUploadStatus();

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file (JPG, PNG, WEBP, GIF).");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Image must be under 8 MB.");
            return;
        }
        setUploading(true);
        beginUpload();
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // Backend returns relative /api/files/... — make it absolute so it works as <img src>
            const absolute = r.data.url.startsWith("http") ? r.data.url : `${BACKEND_URL}${r.data.url}`;
            onChange(absolute);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            endUpload();
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div data-testid={`${testIdPrefix}-upload`}>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">{label}</label>
            <div className="flex gap-2">
                <input
                    type="text"
                    placeholder="https://… or use Upload"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="flex-1 px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush text-sm"
                    data-testid={`${testIdPrefix}-url-input`}
                />
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 px-4 py-3 bg-ink text-white text-[10px] tracking-[0.2em] uppercase hover:bg-blush transition-colors disabled:opacity-60"
                    data-testid={`${testIdPrefix}-upload-btn`}
                >
                    <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
                </button>
                <button
                    type="button"
                    onClick={() => setLibraryOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-3 border border-[#EAE3DE] text-ink text-[10px] tracking-[0.2em] uppercase hover:border-blush hover:text-blush transition-colors"
                    data-testid={`${testIdPrefix}-library-btn`}
                >
                    <Library size={14} /> Library
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={onFile}
                    className="hidden"
                    data-testid={`${testIdPrefix}-file-input`}
                />
            </div>
            {libraryOpen && (
                <MediaLibraryModal
                    onSelect={(url) => {
                        onChange(url);
                        setLibraryOpen(false);
                    }}
                    onClose={() => setLibraryOpen(false)}
                />
            )}
            {value && (
                <div className="mt-3 relative inline-block">
                    <img src={value} alt="Preview" className="w-32 h-32 object-cover border border-[#EAE3DE]" />
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="absolute -top-2 -right-2 bg-white border border-[#EAE3DE] rounded-full p-1 hover:bg-blush hover:text-white"
                        data-testid={`${testIdPrefix}-clear`}
                    >
                        <X size={12} />
                    </button>
                </div>
            )}
            {value && <FocalPointPicker src={value} testIdPrefix={testIdPrefix} />}
        </div>
    );
}
